// src/pages/OrganizationMembersPage.jsx

import React, { useEffect, useState } from "react";
import { FRONT_URL } from "../config/apiConfig";
import { useSelector } from "react-redux";
import {
  getOrgInfo,
  removeUserFromOrg,
  updateUserRoleInOrg
} from "../services/organizationService";
import {
  createInvitation,
  getInvitationsForOrg,
  cancelInvitation
} from "../services/invitationService";
import {
  getJoinRequestsForOrganization,
  approveJoinRequest,
  rejectJoinRequest
} from "../services/joinRequestService";
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Select,
  MenuItem,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Avatar,
  Tabs,
  Tab,
  Divider,
  Stack,
  TableContainer,
  Menu,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Skeleton
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MailOutline from "@mui/icons-material/MailOutline";
import PeopleOutline from "@mui/icons-material/PeopleOutline";
import PersonAddOutlined from "@mui/icons-material/PersonAddOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useSnackbar } from "notistack";
import AddMemberDialog from "../components/AddMemberDialog";

// Skeleton Components
const MemberTableRowSkeleton = () => (
  <TableRow>
    <TableCell>
      <Stack direction="row" spacing={2.5} alignItems="center">
        <Skeleton variant="circular" width={46} height={46} />
        <Skeleton variant="text" width={120} height={20} />
      </Stack>
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={180} height={20} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={80} height={20} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={60} height={20} />
    </TableCell>
  </TableRow>
);

const MemberCardSkeleton = () => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
          <Skeleton variant="circular" width={46} height={46} />
          <Stack spacing={0.5}>
            <Skeleton variant="text" width={120} height={20} />
            <Skeleton variant="text" width={160} height={16} />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Skeleton variant="text" width={60} height={20} />
          <Skeleton variant="text" width={60} height={20} />
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

const InvitationRowSkeleton = () => (
  <TableRow>
    <TableCell>
      <Skeleton variant="text" width={180} height={20} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={80} height={20} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={100} height={20} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={60} height={20} />
    </TableCell>
  </TableRow>
);

const JoinRequestRowSkeleton = () => (
  <TableRow>
    <TableCell>
      <Stack direction="row" spacing={2.5} alignItems="center">
        <Skeleton variant="circular" width={46} height={46} />
        <Skeleton variant="text" width={120} height={20} />
      </Stack>
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={180} height={20} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={100} height={20} />
    </TableCell>
    <TableCell>
      <Stack direction="row" spacing={1}>
        <Skeleton variant="text" width={60} height={20} />
        <Skeleton variant="text" width={60} height={20} />
      </Stack>
    </TableCell>
  </TableRow>
);

function OrganizationMembersPage() {
  const { user } = useSelector((state) => state.user);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [org, setOrg] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [joinRequestsLoading, setJoinRequestsLoading] = useState(false);

  // tab state
  const [tabValue, setTabValue] = useState(0);

  // invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("user");

  // open invitations
  const [invitations, setInvitations] = useState([]);

  // join requests
  const [joinRequests, setJoinRequests] = useState([]);

  // remove user dialog
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeUserId, setRemoveUserId] = useState(null);
  const [removeUserName, setRemoveUserName] = useState("");

  // cancel invitation dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelInvId, setCancelInvId] = useState(null);
  const [cancelInvEmail, setCancelInvEmail] = useState("");

  // role menu
  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // add member dialog
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);

  useEffect(() => {
    fetchOrganization();
    // eslint-disable-next-line
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  function showSuccess(msg) {
    enqueueSnackbar(msg, { variant: "success" });
  }
  function showError(msg) {
    enqueueSnackbar(msg, { variant: "error" });
  }

  const handleRoleMenuOpen = (event, member) => {
    setRoleMenuAnchor(event.currentTarget);
    setSelectedMember(member);
  };

  const handleRoleMenuClose = () => {
    setRoleMenuAnchor(null);
    setSelectedMember(null);
  };

  const handleRoleSelect = (newRole) => {
    if (selectedMember) {
      handleRoleChange(selectedMember._id, newRole);
    }
    handleRoleMenuClose();
  };

  const handleAddMemberClick = () => {
    setAddMemberDialogOpen(true);
  };

  const handleAddMemberClose = () => {
    setAddMemberDialogOpen(false);
  };

  const handleCopyInvitationLink = () => {
    if (org && org._id) {
      const inviteLink = `${FRONT_URL}/join-org/${org._id}`;
      copyToClipboard(inviteLink);
    }
  };

  async function fetchOrganization() {
    try {
      setLoading(true);
      setError("");
      if (!user || !user.currentOrganization || !user.currentOrganization._id) {
        setError("No organization found");
        setLoading(false);
        return;
      }
      const orgId = user.currentOrganization._id;
      const data = await getOrgInfo(orgId);
      setOrg(data);
      setLoading(false);
      fetchInvitations(orgId);
      fetchJoinRequests(orgId);
    } catch (err) {
      setError("Failed to load organization");
      showError("Failed to load organization");
      setLoading(false);
    }
  }

  async function fetchInvitations(orgId) {
    try {
      setInvitationsLoading(true);
      const invs = await getInvitationsForOrg(orgId);
      setInvitations(invs);
      setInvitationsLoading(false);
    } catch (err) {
      showError("Failed to load invitations");
      setInvitationsLoading(false);
    }
  }

  async function fetchJoinRequests(orgId) {
    try {
      setJoinRequestsLoading(true);
      const requests = await getJoinRequestsForOrganization(orgId);
      setJoinRequests(requests);
      setJoinRequestsLoading(false);
    } catch (err) {
      showError("Failed to load join requests");
      setJoinRequestsLoading(false);
    }
  }

  async function handleRemoveConfirmed() {
    if (!org || !org._id || !removeUserId) return;
    try {
      await removeUserFromOrg(org._id, removeUserId);
      showSuccess("User removed");
      fetchOrganization();
    } catch (err) {
      showError("Failed to remove user");
    }
    closeRemoveDialog();
  }

  function openRemoveDialog(memberId, memberName) {
    setRemoveUserId(memberId);
    setRemoveUserName(memberName);
    setRemoveDialogOpen(true);
  }

  function closeRemoveDialog() {
    setRemoveDialogOpen(false);
    setRemoveUserId(null);
    setRemoveUserName("");
  }

  async function handleRoleChange(memberId, newRole) {
    if (!org || !org._id) return;
    try {
      await updateUserRoleInOrg(org._id, memberId, newRole);
      showSuccess("Role updated");
      fetchOrganization();
    } catch (err) {
      showError("Failed to update role");
    }
  }

  async function handleInvite(e) {
    e.preventDefault();
    if (!org || !org._id) return;
    try {
      const data = await createInvitation(org._id, inviteEmail, inviteRole);
      showSuccess(data.message);
      if (data.link) {
        enqueueSnackbar(`Invitation link: ${data.link}`, { variant: "info" });
      }
      setInviteEmail("");
      setInviteRole("user");
      fetchInvitations(org._id);
      setAddMemberDialogOpen(false); // Close the dialog after successful invite
    } catch (err) {
      showError("Failed to create invitation");
    }
  }

  async function handleCancelInvitationConfirmed() {
    if (!cancelInvId) return;
    try {
      await cancelInvitation(cancelInvId);
      showSuccess("Invitation canceled");
      if (org && org._id) fetchInvitations(org._id);
    } catch (err) {
      showError("Failed to cancel invitation");
    }
    closeCancelDialog();
  }

  function openCancelDialog(invId, invEmail) {
    setCancelInvId(invId);
    setCancelInvEmail(invEmail);
    setCancelDialogOpen(true);
  }

  function closeCancelDialog() {
    setCancelDialogOpen(false);
    setCancelInvId(null);
    setCancelInvEmail("");
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(
      () => {
        showSuccess("Copied to clipboard");
      },
      () => {
        showError("Failed to copy");
      }
    );
  }

  async function handleApproveJoinRequest(requestId) {
    try {
      await approveJoinRequest(requestId);
      showSuccess("Join request approved");
      fetchOrganization(); // Refresh data
    } catch (err) {
      showError("Failed to approve join request");
    }
  }

  async function handleRejectJoinRequest(requestId) {
    try {
      await rejectJoinRequest(requestId);
      showSuccess("Join request rejected");
      if (org && org._id) fetchJoinRequests(org._id);
    } catch (err) {
      showError("Failed to reject join request");
    }
  }

  // Get user initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return '?';
    const words = name.split(' ');
    if (words.length === 1) return name.charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  // Generate consistent avatar color based on user ID
  const getAvatarColor = (userId, name) => {
    const colors = theme.palette.custom.avatarColors;
    
    const seed = userId || name || '';
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  // Tab data with dynamic counts
  const tabsData = [
    {
      label: "All Members",
      icon: <PeopleOutline fontSize="small" />,
      count: org?.users?.length || 0,
    },
    {
      label: "Pending Invitations",
      icon: <MailOutline fontSize="small" />,
      count: invitations.length,
    },
    {
      label: "Join Requests",
      icon: <PersonAddOutlined fontSize="small" />,
      count: joinRequests.length,
    },
  ];

  const renderMobileCard = (member, entry) => (
    <Card key={member._id} sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Avatar
            src={member.avatar?.thumbnailUrl || member.avatar?.url}
            alt={member.username}
            sx={{ 
              width: 46, 
              height: 46,
              bgcolor: !member.avatar?.url ? getAvatarColor(member._id, member.username) : undefined,
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {getInitials(member.username)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" color="text.primary">
              {member.username}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {member.email}
            </Typography>
          </Box>
        </Stack>
        
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Role
            </Typography>
            {member._id === user._id ? (
              <Typography color="text.primary">
                {entry.role}
              </Typography>
            ) : (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography color="text.primary">
                  {entry.role}
                </Typography>
                <KeyboardArrowDownIcon 
                  fontSize="small" 
                  onClick={(e) => handleRoleMenuOpen(e, member)}
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>
            )}
          </Box>
          
          <Stack direction="row" spacing={1}>
            {member._id !== user._id && (
              <>
                <Button
                  variant="text"
                  sx={{
                    color: "text.primary",
                    fontSize: '0.875rem'
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="text"
                  onClick={() => openRemoveDialog(member._id, member.username)}
                  sx={{
                    color: "custom.danger",
                    fontSize: '0.875rem'
                  }}
                >
                  Remove
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading || !org) {
    return (
      <Container sx={{ mt: 3, px: isMobile ? 2 : 3 }}>
        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
        
        {!error && (
          <Box>
            {/* Header Skeleton */}
            <Box display="flex" flexDirection="column" gap="13px" width="100%">
              <Box display="flex" flexDirection="column" gap="19px" width="100%">
                <Skeleton variant="text" width={300} height={40} />
                
                <Box display="flex" 
                  flexDirection={{ xs: "column", sm: "row" }} 
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  gap={2}
                >
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Skeleton variant="rounded" width={120} height={40} />
                    <Skeleton variant="rounded" width={140} height={40} />
                    <Skeleton variant="rounded" width={130} height={40} />
                  </Box>
                  <Skeleton variant="rounded" width={160} height={43} />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ backgroundColor: "divider", width: "100%", mt: 2, mb: 3 }} />

            {/* Content Skeleton */}
            {isMobile ? (
              <Box>
                {[...Array(4)].map((_, index) => (
                  <MemberCardSkeleton key={index} />
                ))}
              </Box>
            ) : (
              <TableContainer component={Box}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><Skeleton variant="text" width={100} height={20} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} height={20} /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} height={20} /></TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(5)].map((_, index) => (
                      <MemberTableRowSkeleton key={index} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 3, px: isMobile ? 2 : 3 }}>
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {/* Header with tabs and add button */}
      <Box display="flex" flexDirection="column" gap="13px" width="100%">
        <Box display="flex" flexDirection="column" gap="19px" width="100%">
          <Typography
            variant="h4"
            color="text.primary"
            sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}
          >
            Organization Members
          </Typography>

          <Box
            display="flex"
            alignItems={isMobile ? "flex-start" : "center"}
            justifyContent="space-between"
            width="100%"
            flexDirection={isMobile ? "column" : "row"}
            gap={isMobile ? 2 : 0}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              TabIndicatorProps={{ style: { display: "none" } }}
              sx={{ 
                minHeight: "unset",
                width: "100%",
                '& .MuiTabs-flexContainer': {
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  flexWrap: 'wrap',
                  gap: { xs: 1, sm: 0 }
                },
                '& .MuiTabs-scroller': {
                  overflow: 'visible !important'
                }
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {tabsData.map((tab, index) => (
                <Tab
                  key={index}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      {tab.icon}
                      <Typography
                        fontSize="14px"
                        color={tabValue === index ? "text.primary" : "text.secondary"}
                      >
                        {tab.label} ({tab.count})
                      </Typography>
                    </Box>
                  }
                  sx={{
                    textTransform: "none",
                    minHeight: "unset",
                    padding: { xs: "8px 16px", sm: "0px" },
                    marginRight: { xs: 0, sm: "40px" },
                    marginBottom: 0,
                    alignItems: 'center',
                    minWidth: { xs: "auto", sm: "unset" },
                    '& .MuiTab-wrapper': {
                      flexDirection: 'row',
                      alignItems: 'center'
                    }
                  }}
                  disableRipple
                />
              ))}
            </Tabs>

            <Button
              variant="contained"
              onClick={handleAddMemberClick}
              sx={{
                backgroundColor: "primary.main",
                borderRadius: "3px",
                boxShadow: theme.customShadows.primaryButton,
                padding: "11px 16px",
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: "160px" },
                height: "43px",
                fontSize: "14px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "primary.dark",
                  boxShadow: theme.customShadows.primaryButtonHover,
                }
              }}
            >
              Add New Member
            </Button>
          </Box>
        </Box>

        <Divider sx={{ backgroundColor: "divider", width: "100%" }} />
      </Box>

      {/* Content based on selected tab */}
      {tabValue === 0 && (
        <Box sx={{ mt: 3 }}>
          {/* Members Table/Cards */}
          {isMobile ? (
            <Box>
              {org.users?.map((entry, index) => {
                const member = entry.user;
                if (!member) return null;
                return (
                  <React.Fragment key={member._id}>
                    {renderMobileCard(member, entry)}
                    {index < org.users.length - 1 && (
                      <Divider sx={{ backgroundColor: "divider", mb: 2 }} />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          ) : (
            <TableContainer component={Box}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        User Name
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        Email
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.primary">
                        Role
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {org.users?.map((entry) => {
                    const member = entry.user;
                    if (!member) return null;
                    
                    return (
                      <React.Fragment key={member._id}>
                        <TableRow>
                          <TableCell>
                            <Stack direction="row" spacing={2.5} alignItems="center">
                              <Avatar
                                src={member.avatar?.thumbnailUrl || member.avatar?.url}
                                alt={member.username}
                                sx={{
                                  width: 46,
                                  height: 46,
                                  bgcolor: !member.avatar?.url ? getAvatarColor(member._id, member.username) : undefined,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              >
                                {getInitials(member.username)}
                              </Avatar>
                              <Typography
                                variant="body1"
                                color="text.primary"
                              >
                                {member.username}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body1"
                              color="text.primary"
                            >
                              {member.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {member._id === user._id ? (
                              <Typography
                                variant="body1"
                                color="text.primary"
                              >
                                {entry.role}
                              </Typography>
                            ) : (
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography
                                  variant="body1"
                                  color="text.primary"
                                >
                                  {entry.role}
                                </Typography>
                                <KeyboardArrowDownIcon 
                                  fontSize="small" 
                                  onClick={(e) => handleRoleMenuOpen(e, member)}
                                  sx={{ cursor: 'pointer' }}
                                />
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell>
                            {member._id === user._id ? (
                              <Typography color="text.secondary">
                                Cannot remove self
                              </Typography>
                            ) : (
                              <Stack
                                direction="row"
                                spacing={2}
                                justifyContent="space-between"
                              >
                                <Button
                                  variant="text"
                                  sx={{
                                    color: "text.primary",
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="text"
                                  onClick={() => openRemoveDialog(member._id, member.username)}
                                  sx={{
                                    color: "custom.danger",
                                  }}
                                >
                                  Remove
                                </Button>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={4} padding="none">
                            <Divider />
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Pending Invitations Tab */}
      {tabValue === 1 && (
        <Box sx={{ mt: 3 }}>
          {invitationsLoading ? (
            isMobile ? (
              <Box>
                {[...Array(3)].map((_, index) => (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Skeleton variant="text" width={180} height={20} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width={100} height={16} sx={{ mb: 2 }} />
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Skeleton variant="text" width={120} height={16} />
                          <Skeleton variant="circular" width={20} height={20} />
                        </Stack>
                        <Skeleton variant="text" width={60} height={20} />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <TableContainer component={Box}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><Skeleton variant="text" width={80} height={20} /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} height={20} /></TableCell>
                      <TableCell><Skeleton variant="text" width={120} height={20} /></TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(3)].map((_, index) => (
                      <InvitationRowSkeleton key={index} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : invitations.length === 0 ? (
            <Typography>No pending invitations</Typography>
          ) : (
            <>
              {isMobile ? (
                <Box>
                  {invitations.map((inv, index) => {
                    const linkUrl = `${FRONT_URL}/invite/${inv.token}`;
                    return (
                      <React.Fragment key={inv._id}>
                        <Card sx={{ mb: 2 }}>
                          <CardContent>
                            <Typography variant="body1" color="text.primary" sx={{ mb: 1 }}>
                              {inv.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Role: {inv.role}
                            </Typography>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {inv.token.slice(0, 15)}...
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => copyToClipboard(linkUrl)}
                                >
                                  <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                              </Box>
                              <Button
                                variant="text"
                                onClick={() => openCancelDialog(inv._id, inv.email)}
                                sx={{
                                  color: "custom.danger",
                                }}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                        {index < invitations.length - 1 && (
                          <Divider sx={{ backgroundColor: "divider", mb: 2 }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              ) : (
                <TableContainer component={Box}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography variant="body2" color="text.primary">
                            Email
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.primary">
                            Role
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.primary">
                            Invitation Link
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invitations.map((inv) => {
                        const linkUrl = `${FRONT_URL}/invite/${inv.token}`;
                        return (
                          <React.Fragment key={inv._id}>
                            <TableRow>
                              <TableCell>
                                <Typography variant="body1" color="text.primary">
                                  {inv.email}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1" color="text.primary">
                                  {inv.role}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {inv.token.slice(0, 20)}...
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(linkUrl)}
                                  >
                                    <ContentCopyIcon fontSize="inherit" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="text"
                                  onClick={() => openCancelDialog(inv._id, inv.email)}
                                  sx={{
                                    color: "custom.danger",
                                  }}
                                >
                                  Cancel
                                </Button>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={4} padding="none">
                                <Divider />
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Box>
      )}

      {/* Join Requests Tab */}
      {tabValue === 2 && (
        <Box sx={{ mt: 3 }}>
          {joinRequestsLoading ? (
            isMobile ? (
              <Box>
                {[...Array(3)].map((_, index) => (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Skeleton variant="circular" width={46} height={46} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width={120} height={20} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width={160} height={16} />
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Skeleton variant="rounded" width={80} height={32} />
                        <Skeleton variant="rounded" width={70} height={32} />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <TableContainer component={Box}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><Skeleton variant="text" width={100} height={20} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} height={20} /></TableCell>
                      <TableCell><Skeleton variant="text" width={120} height={20} /></TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(3)].map((_, index) => (
                      <JoinRequestRowSkeleton key={index} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : joinRequests.length === 0 ? (
            <Typography>No pending join requests</Typography>
          ) : (
            <>
              {isMobile ? (
                <Box>
                  {joinRequests.map((request, index) => {
                    const requestUser = request.userId;
                    return (
                      <React.Fragment key={request._id}>
                        <Card sx={{ mb: 2 }}>
                          <CardContent>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                              <Avatar
                                src={requestUser?.avatar?.thumbnailUrl || requestUser?.avatar?.url}
                                alt={requestUser?.username || 'Unknown'}
                                sx={{ 
                                  width: 46, 
                                  height: 46,
                                  bgcolor: !requestUser?.avatar?.url ? getAvatarColor(requestUser?._id, requestUser?.username) : undefined,
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              >
                                {getInitials(requestUser?.username || 'Unknown')}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" color="text.primary">
                                  {requestUser?.username || 'Unknown'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {requestUser?.email || 'Unknown'}
                                </Typography>
                              </Box>
                            </Stack>
                            
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => handleApproveJoinRequest(request._id)}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleRejectJoinRequest(request._id)}
                              >
                                Reject
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                        {index < joinRequests.length - 1 && (
                          <Divider sx={{ backgroundColor: "divider", mb: 2 }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              ) : (
                <TableContainer component={Box}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography variant="body2" color="text.primary">
                            User
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.primary">
                            Email
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {joinRequests.map((request) => {
                        const requestUser = request.userId;
                        
                        return (
                          <React.Fragment key={request._id}>
                            <TableRow>
                              <TableCell>
                                <Stack direction="row" spacing={2.5} alignItems="center">
                                  <Avatar
                                    src={requestUser?.avatar?.thumbnailUrl || requestUser?.avatar?.url}
                                    alt={requestUser?.username || 'Unknown'}
                                    sx={{ 
                                      width: 46, 
                                      height: 46,
                                      bgcolor: !requestUser?.avatar?.url ? getAvatarColor(requestUser?._id, requestUser?.username) : undefined,
                                      color: 'white',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {getInitials(requestUser?.username || 'Unknown')}
                                  </Avatar>
                                  <Typography variant="body1" color="text.primary">
                                    {requestUser?.username || 'Unknown'}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1" color="text.primary">
                                  {requestUser?.email || 'Unknown'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    onClick={() => handleApproveJoinRequest(request._id)}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => handleRejectJoinRequest(request._id)}
                                  >
                                    Reject
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={3} padding="none">
                                <Divider />
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Box>
      )}

      {/* Role Selection Menu */}
      <Menu
        anchorEl={roleMenuAnchor}
        open={Boolean(roleMenuAnchor)}
        onClose={handleRoleMenuClose}
      >
        <MenuItem onClick={() => handleRoleSelect("user")}>
          <Typography>User</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleRoleSelect("admin")}>
          <Typography>Admin</Typography>
        </MenuItem>
      </Menu>

      {/* INVITE NEW MEMBER FORM - Hidden for now, will be popup */}
      <Box sx={{ display: 'none', mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Invite New Member
        </Typography>
        <Box
          component="form"
          onSubmit={handleInvite}
          sx={{ display: "flex", gap: 2, maxWidth: 400, flexWrap: 'wrap' }}
        >
          <TextField
            label="Email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
            sx={{ minWidth: 200 }}
          />
          <Select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            sx={{ minWidth: 100 }}
          >
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
          <Button type="submit" variant="contained">
            Invite
          </Button>
        </Box>
      </Box>

      {/* REMOVE USER DIALOG */}
      <Dialog open={removeDialogOpen} onClose={closeRemoveDialog}>
        <DialogTitle>
          <Typography>Confirm User Removal</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography>
              Are you sure you want to remove user "{removeUserName}" from the org?
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemoveDialog}>Cancel</Button>
          <Button onClick={handleRemoveConfirmed} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* CANCEL INVITATION DIALOG */}
      <Dialog open={cancelDialogOpen} onClose={closeCancelDialog}>
        <DialogTitle>
          <Typography>Confirm Invitation Cancellation</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography>
              Are you sure you want to cancel the invitation for "{cancelInvEmail}"?
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog}>Back</Button>
          <Button onClick={handleCancelInvitationConfirmed} color="error" variant="contained">
            Cancel Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADD MEMBER DIALOG */}
      <AddMemberDialog
        open={addMemberDialogOpen}
        onClose={handleAddMemberClose}
        onInvite={handleInvite}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        inviteRole={inviteRole}
        setInviteRole={setInviteRole}
        onCopyInvitationLink={handleCopyInvitationLink}
      />
    </Container>
  );
}

export default OrganizationMembersPage;
